/**
 * Words and colours for the interview availability light — pure, so the Radar
 * and the action board cannot describe the same number two ways.
 */
import type {
  AvailabilityLight,
  AvailabilityReason,
  AvailabilityRound,
  AvailabilityStage,
} from '../../types/interviewAvailability';

const UNIT: Record<AvailabilityRound, string> = {
  First: 'first interview',
  Personal: 'personal interview',
  Case: 'case interview',
  Final: 'final round',
};

/** Solid fill per light. Black is a real fourth state, not "no data" — that one is grey. */
export const LIGHT_DOT: Record<AvailabilityLight, string> = {
  green: 'bg-green',
  orange: 'bg-orange',
  red: 'bg-red',
  black: 'bg-black',
};

export const LIGHT_LABEL: Record<AvailabilityLight, string> = {
  green: '7 or more free slots',
  orange: '4–6 free slots',
  red: '2–3 free slots',
  black: '0–1 free slots',
};

/** The reason code, whichever variant the stage came from (the board's carries no `reason`). */
export function reasonOf(stage: AvailabilityStage | null | undefined): AvailabilityReason | null {
  if (!stage) return 'not-evaluated';
  if (stage.reason !== undefined) return stage.reason ?? null;
  if (stage.notApplicable) return 'not-applicable';
  if (stage.notInProcess) return 'not-in-process';
  if (!stage.panelSource) return 'no-panel';
  if (stage.error === 'calendar not readable') return 'calendar-unreadable';
  if (stage.error) return 'not-evaluated';
  if (stage.diagnosis) return 'out-of-office';
  return null;
}

/** Whether a round gets a lamp at all: not for a round the role does not run. */
export function isShown(stage: AvailabilityStage | null | undefined): boolean {
  const reason = reasonOf(stage);
  return reason !== 'not-applicable' && reason !== 'not-in-process';
}

/**
 * "Counted from 2 links · 2 interviewers · 8 days · 30 min" — counts only, never names, so it is
 * safe on the open Cockpit. Two similar roles with different numbers usually differ right here.
 */
export function basisLabel(stage: AvailabilityStage | null | undefined): string | null {
  const b = stage?.basis;
  if (!b) return null;
  const n = (count: number | undefined, one: string) =>
    count == null ? null : `${count} ${one}${count === 1 ? '' : 's'}`;
  const parts = [
    n(b.links, 'link'),
    n(b.interviewers, 'interviewer'),
    b.days != null ? `${b.days} days` : null,
    b.minutes ? `${b.minutes} min` : null,
  ].filter(Boolean);
  return parts.length ? `Counted from ${parts.join(' · ')}` : null;
}

/** The short text beside the dot: "9 slots", or what stopped us counting. */
export function shortLabel(stage: AvailabilityStage | null | undefined, round?: AvailabilityRound): string {
  const reason = reasonOf(stage);
  if (stage?.light && stage.free != null) return `${stage.free} ${stage.free === 1 ? 'slot' : 'slots'}`;
  if (reason === 'no-panel') return round === 'First' || round === 'Personal' ? 'no interview link' : 'no panel set';
  if (reason === 'calendar-unreadable') return 'calendar unreadable';
  return 'not checked';
}

/** "+4 with a move", or null when moving meetings would not add any. */
export function withMoveLabel(stage: AvailabilityStage | null | undefined): string | null {
  return stage?.withMove ? `+${stage.withMove} with a move` : null;
}

/** "Thu 15 Oct" from YYYY-MM-DD, read as a calendar date (no time zone shift). */
export function dayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * The full sentence, for a tooltip or a detail line. Says what was counted,
 * until when, and with whose panel — the three things a reader needs to trust
 * a colour.
 */
export function describeStage(
  stage: AvailabilityStage | null | undefined,
  round: AvailabilityRound,
  windowEnd: string
): string {
  const until = dayLabel(windowEnd);
  const reason = reasonOf(stage);
  if (reason === 'not-applicable') return `${round}: no final round for this role.`;
  if (reason === 'not-in-process') return `${round}: not part of this role's process.`;
  const waiting = stage?.waiting
    ? ` ${stage.waiting} candidate${stage.waiting === 1 ? ' is' : 's are'} waiting in this round.`
    : '';
  if (reason === 'no-panel' && (round === 'First' || round === 'Personal')) {
    return `${round}: no live interview link for this round in CCA.${waiting}`;
  }
  if (reason === 'no-panel') {
    return (
      (round === 'Case'
        ? 'Case: no panel set on the board, and none found in the kick-off sheet yet.'
        : 'Final: no round template set, and the kick-off sheet does not name both seats yet.') + waiting
    );
  }
  if (reason === 'calendar-unreadable') return `${round}: a panelist's calendar could not be read.`;
  if (!stage?.light || stage.free == null) return `${round}: not checked.`;
  const unit = UNIT[round];
  const fromLinks = stage.panelSource === 'link';
  const parts = [
    fromLinks
      ? `${round}: ${stage.free} ${unit}${stage.free === 1 ? '' : 's'} bookable through the CCA interview link${stage.free === 1 ? '' : 's'}, today until ${until}.`
      : `${round}: ${stage.free} ${unit}${stage.free === 1 ? '' : 's'} bookable with everyone free, today until ${until}.`,
  ];
  if (stage.partial) parts.push('Some of its links could not be read, so this is at least the number.');
  if (stage.withMove) parts.push(`${stage.withMove} more if someone moves a meeting.`);
  if (stage.panelSource === 'kickoff') parts.push('Counted with the panel named in the kick-off sheet, not yet confirmed on the board.');
  if (stage.diagnosis) parts.push(stage.diagnosis);
  else if (reason === 'out-of-office') parts.push('Someone on the panel is out of office.');
  const basis = basisLabel(stage);
  if (basis) parts.push(`${basis}.`);
  return parts.join(' ');
}
